import { postSiteEdit } from './editing-agent-api';
import {
  getSiteHomeCode,
  listSiteVirtualFiles,
  postGenerateSiteImage,
  type SiteVirtualFile,
} from './site-media-api';

export type HostToolResult =
  | { ok: true; result: unknown }
  | { ok: false; error: string };

export type CanvasHostAction =
  | 'generate_site_image'
  | 'list_site_files'
  | 'read_site_file'
  | 'write_site_file';

export interface CanvasHostActionContext {
  siteId: string | null;
}

function missingSite(): HostToolResult {
  return { ok: false, error: 'No active site — generate a site first.' };
}

/** Execute a host-side canvas action invoked by agentable tenant tools. */
export async function executeCanvasHostAction(
  action: CanvasHostAction,
  args: Record<string, unknown>,
  context: CanvasHostActionContext,
): Promise<HostToolResult> {
  const { siteId } = context;
  if (!siteId) {
    return missingSite();
  }

  switch (action) {
    case 'generate_site_image': {
      const prompt = typeof args.prompt === 'string' ? args.prompt : '';
      const useContext = args.useContext !== false;
      try {
        const result = await postGenerateSiteImage({ siteId, prompt, useContext });
        const path = result.filename ? `assets/${result.filename}` : 'assets/generated.png';
        return {
          ok: true,
          result: {
            path,
            name: result.filename ?? 'generated.png',
            url: result.imageUrl,
            promptUsed: result.promptUsed,
          },
        };
      } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : 'Image generation failed' };
      }
    }
    case 'list_site_files': {
      try {
        const files = await listSiteVirtualFiles(siteId);
        return { ok: true, result: files satisfies SiteVirtualFile[] };
      } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : 'File listing failed' };
      }
    }
    case 'read_site_file': {
      const path = typeof args.path === 'string' ? args.path : '';
      if (!path) {
        return { ok: false, error: 'path is required' };
      }
      try {
        if (path === 'index.html' || path.endsWith('.html')) {
          const html = await getSiteHomeCode(siteId);
          return { ok: true, result: { path, content: html, kind: 'page' as const } };
        }
        const files = await listSiteVirtualFiles(siteId);
        const match = files.find((file) => file.path === path);
        if (!match) {
          return { ok: false, error: `Unknown path "${path}"` };
        }
        return { ok: true, result: match };
      } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : 'File read failed' };
      }
    }
    case 'write_site_file': {
      const instruction = typeof args.instruction === 'string' ? args.instruction : '';
      const page = typeof args.page === 'string' ? args.page : 'index.html';
      if (!instruction.trim()) {
        return { ok: false, error: 'instruction is required' };
      }
      try {
        const result = await postSiteEdit({ siteId, instruction, page });
        return {
          ok: true,
          result: {
            replaced: result.replaced,
            verified: result.verified,
            screenshot_url: result.screenshot_url,
          },
        };
      } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : 'Edit failed' };
      }
    }
    default:
      return { ok: false, error: `Unknown host action "${action as string}"` };
  }
}
